import styled from "@emotion/styled";

import { BLINKING_CLASS, useCloudFace } from "./useCloudFace";

/**
 * The not-found cloud, using the marketing site's `Error.svg` geometry so this
 * 404 and the ones on www.stotles.com and the SEO surface look alike. Inlined
 * rather than shipped as an asset so the fills come from theme tokens instead of
 * that file's hardcoded blues. Decorative, so `aria-hidden`.
 *
 * The face follows the pointer and blinks (`useCloudFace`) as progressive
 * enhancement: without JS, or under reduced motion, it renders static.
 */
export function NotFoundCloud() {
  const { svgRef, faceRef } = useCloudFace();

  return (
    <Cloud
      ref={svgRef}
      width="164"
      height="132"
      viewBox="0 0 164 132"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
      data-testid="cloud"
    >
      <path
        className="cloud-body"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M32.75 126C17.5621 126 5.25 113.967 5.25 99.125C5.25 84.2825 17.5621 72.25 32.75 72.25C33.3959 72.25 34.0365 72.2717 34.6713 72.3146C34.2303 69.8597 34 67.3317 34 64.75C34 41.2779 53.0275 22.25 76.5 22.25C95.2388 22.25 111.145 34.3775 116.799 51.2119C118.137 51.0719 119.498 51 120.875 51C141.931 51 159 67.7894 159 88.5C159 108.176 143.594 124.409 124 125.972V126H53.385H32.75Z"
      />
      <path
        className="cloud-outline"
        d="M47.745 126H39.0393M32.75 126C17.5621 126 5.25 113.967 5.25 99.125C5.25 84.2825 17.5621 72.25 32.75 72.25C33.3959 72.25 34.0365 72.2717 34.6713 72.3146C34.2303 69.8597 34 67.3317 34 64.75C34 41.2779 53.0275 22.25 76.5 22.25C95.2388 22.25 111.145 34.3775 116.799 51.2119C118.137 51.0719 119.498 51 120.875 51C141.931 51 159 67.7894 159 88.5C159 108.176 143.594 124.409 124 125.972V126H53.385H32.75Z"
      />

      {/* Shadow. The mouth carries its own fill rather than being punched out of
          this path, so the face can move without sliding off its own hole. */}
      <path
        className="cloud-shadow"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M63.515 31.4282C63.515 71.7071 99.7912 104.898 146.5 109.379C140.646 116.576 131.608 121.46 121.309 122.226V122.25H37.4289C27.2516 122.25 10.25 117.962 10.25 99.185C10.25 80.4087 23.4678 76.1213 37.4289 76.1213C38.0225 76.1213 38.6114 76.14 39.1949 76.1762C38.7895 74.0695 38.7183 71.898 38.5779 69.6843C37.6298 54.7303 44.1537 35.8838 63.5375 29.75C63.5225 30.3091 63.515 30.868 63.515 31.4282Z"
      />

      <g className="cloud-face-group" ref={faceRef} data-testid="cloud-face">
        <path
          className="cloud-face cloud-face--mouth"
          d="M77.125 108.5C80.9225 108.5 84 105.423 84 101.625C84 97.8275 80.9225 94.75 77.125 94.75C73.3275 94.75 70.25 97.8275 70.25 101.625C70.25 105.423 73.3275 108.5 77.125 108.5Z"
        />
        <path className="cloud-face cloud-eye" d="M57.75 87.2507L66.5 80.3844L57.75 73.8477" />
        <path className="cloud-face cloud-eye" d="M96.5 87.2507L87.75 80.3844L96.5 73.8477" />

        {/* Shut lids, swapped in for the chevrons for the length of a blink. */}
        <path className="cloud-face cloud-eye-shut" d="M57.75 80.3844H66.5" />
        <path className="cloud-face cloud-eye-shut" d="M87.75 80.3844H96.5" />
      </g>

      <path className="cloud-spark" d="M89 34.75C97.1362 36.9032 103.56 43.2661 105.8 51.3665" />
      <path
        className="cloud-spark cloud-spark--thin"
        d="M115.25 13.5C117.321 13.5 119 11.8211 119 9.75C119 7.67887 117.321 6 115.25 6C113.179 6 111.5 7.67887 111.5 9.75C111.5 11.8211 113.179 13.5 115.25 13.5Z"
      />
      <path
        className="cloud-spark--solid"
        d="M154 33.5C156.071 33.5 157.75 31.8211 157.75 29.75C157.75 27.6789 156.071 26 154 26C151.929 26 150.25 27.6789 150.25 29.75C150.25 31.8211 151.929 33.5 154 33.5Z"
      />
      <path
        className="cloud-spark cloud-spark--round"
        d="M124.947 23.1953L135.396 33.6432M135.555 23.1953L125.106 33.6432"
      />
      <path
        className="cloud-spark cloud-spark--round"
        d="M7.00586 45.4961L14.5059 52.9961M14.5059 45.4961L7.00586 52.9961"
      />
      <path
        className="cloud-spark--solid"
        d="M24 37.25C26.0711 37.25 27.75 35.5711 27.75 33.5C27.75 31.4289 26.0711 29.75 24 29.75C21.9289 29.75 20.25 31.4289 20.25 33.5C20.25 35.5711 21.9289 37.25 24 37.25Z"
      />
    </Cloud>
  );
}

const Cloud = styled.svg(({ theme }) => ({
  display: "block",
  width: "164px",
  maxWidth: "100%",
  height: "auto",

  ".cloud-body": { fill: theme.colors.sysBackgroundDefault },
  ".cloud-shadow": { fill: theme.colors.sysPrimarySubtleAlt },

  ".cloud-outline, .cloud-face": {
    fill: "none",
    stroke: theme.colors.sysPrimaryDefault,
    strokeWidth: 3.125,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },

  ".cloud-face--mouth": { fill: theme.colors.sysBackgroundDefault },

  ".cloud-spark": {
    fill: "none",
    stroke: theme.colors.sysBrandBlueDefault,
    strokeWidth: 3.125,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  },
  ".cloud-spark--thin": { strokeWidth: 2.5 },
  ".cloud-spark--solid": { fill: theme.colors.sysBrandBlueDefault },

  // Eases the jump between pointer positions. Short enough to read as tracking
  // the cursor rather than lagging behind it.
  ".cloud-face-group": { transition: "transform 140ms ease-out" },

  // Blink: hide the chevrons, show the lids. A hard swap rather than a fade,
  // because that is how fast a real blink is.
  ".cloud-eye-shut": { display: "none" },
  [`&.${BLINKING_CLASS}`]: {
    ".cloud-eye": { display: "none" },
    ".cloud-eye-shut": { display: "inline" },
  },

  // The hook attaches nothing under reduced motion, so this cannot fire. Belt
  // and braces in case a future caller skips that check.
  "@media (prefers-reduced-motion: reduce)": {
    ".cloud-face-group": { transition: "none" },
  },
}));
